import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _assertThisInitialized from "@babel/runtime/helpers/assertThisInitialized";
import _inherits from "@babel/runtime/helpers/inherits";
import _possibleConstructorReturn from "@babel/runtime/helpers/possibleConstructorReturn";
import _getPrototypeOf from "@babel/runtime/helpers/getPrototypeOf";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
var _excluded = ["retry"],
    _excluded2 = ["__serialized"],
    _excluded3 = ["__serialized"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

import _regeneratorRuntime from "@babel/runtime/regenerator";

function _createSuper(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct(); return function _createSuperInternal() { var Super = _getPrototypeOf(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn(this, result); }; }

function _isNativeReflectConstruct() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); return true; } catch (e) { return false; } }

/*
 * Copyright 2023 New Relic Corporation. All rights reserved.
 * SPDX-License-Identifier: Apache-2.0
 */

/**
 * @file Records, aggregates, and harvests session replay data.
 */
import { registerHandler } from '../../../common/event-emitter/register-handler';
import { HarvestScheduler } from '../../../common/harvest/harvest-scheduler';
import { ABORT_REASONS, FEATURE_NAME, QUERY_PARAM_PADDING, RRWEB_EVENT_TYPES, SR_EVENT_EMITTER_TYPES, TRIGGERS } from '../constants';
import { AggregateBase } from '../../utils/aggregate-base';
import { sharedChannel } from '../../../common/constants/shared-channel';
import { obj as encodeObj } from '../../../common/url/encode';
import { warn } from '../../../common/util/console';
import { globalScope } from '../../../common/constants/runtime';
import { SUPPORTABILITY_METRIC_CHANNEL } from '../../metrics/constants';
import { handle } from '../../../common/event-emitter/handle';
import { FEATURE_NAMES, FEATURE_TO_ENDPOINT } from '../../../loaders/features/features';
import { RRWEB_VERSION } from "../../../common/constants/env.npm";
import { MODE, SESSION_EVENTS, SESSION_EVENT_TYPES } from '../../../common/session/constants';
import { stringify } from '../../../common/util/stringify';
import { stylesheetEvaluator } from '../shared/stylesheet-evaluator';
import { now } from '../../../common/timing/now';
import { buildNRMetaNode } from '../shared/utils';
import { MAX_PAYLOAD_SIZE } from '../../../common/constants/agent-constants';
import { cleanURL } from '../../../common/url/clean-url';
export var Aggregate = /*#__PURE__*/function (_AggregateBase) {
  _inherits(Aggregate, _AggregateBase);

  var _super = _createSuper(Aggregate);

  // pass the recorder into the aggregator
  function Aggregate(agentRef, args) {
    var _this;

    _classCallCheck(this, Aggregate);

    _this = _super.call(this, agentRef, FEATURE_NAME);
    /** The interval to harvest at.  This gets overridden if the size of the payload exceeds certain thresholds */

    _defineProperty(_assertThisInitialized(_this), "mode", MODE.OFF);

    _this.harvestTimeSeconds = agentRef.init.session_replay.harvestTimeSeconds || 60;
    /** Set once the recorder has fully initialized after flag checks and sampling */

    _this.initialized = false;
    /** Set once the feature has been "aborted" to prevent other side-effects from continuing */

    _this.blocked = false;
    /** populated with the gzipper lib async */

    _this.gzipper = undefined;
    /** populated with the u8 string lib async */

    _this.u8 = undefined;
    /** set by BCS response */

    _this.entitled = false;
    /** set at BCS response, stored in runtime */

    _this.timeKeeper = undefined;
    _this.recorder = args === null || args === void 0 ? void 0 : args.recorder;
    _this.errorNoticed = (args === null || args === void 0 ? void 0 : args.errorNoticed) || false;
    handle(SUPPORTABILITY_METRIC_CHANNEL, ['Config/SessionReplay/Enabled'], undefined, FEATURE_NAMES.metrics, _this.ee); // The SessionEntity class can emit a message indicating the session was cleared and reset (expiry, inactivity). This feature must abort and never resume if that occurs.

    _this.ee.on(SESSION_EVENTS.RESET, function () {
      _this.abort(ABORT_REASONS.RESET);
    }); // The SessionEntity class can emit a message indicating the session was paused (visibility change). This feature must stop recording if that occurs.


    _this.ee.on(SESSION_EVENTS.PAUSE, function () {
      var _this$recorder;

      (_this$recorder = _this.recorder) === null || _this$recorder === void 0 ? void 0 : _this$recorder.stopRecording();
    }); // The SessionEntity class can emit a message indicating the session was resumed (visibility change). This feature must start running again (if already running) if that occurs.


    _this.ee.on(SESSION_EVENTS.RESUME, function () {
      var _this$recorder2;

      if (!_this.recorder) return; // if the mode changed on a different tab, it needs to update this instance to match

      _this.mode = agentRef.runtime.session.state.sessionReplayMode;
      if (!_this.initialized || _this.mode === MODE.OFF) return;
      (_this$recorder2 = _this.recorder) === null || _this$recorder2 === void 0 ? void 0 : _this$recorder2.startRecording();
    });

    _this.ee.on(SESSION_EVENTS.UPDATE, function (type, data) {
      if (!_this.recorder || !_this.initialized || _this.blocked || type !== SESSION_EVENT_TYPES.CROSS_TAB) return;
      if (_this.mode !== MODE.OFF && data.sessionReplayMode === MODE.OFF) _this.abort(ABORT_REASONS.CROSS_TAB);
      _this.mode = data.sessionReplay;
    }); // Bespoke logic for blobs endpoint.


    _this.scheduler = new HarvestScheduler(FEATURE_TO_ENDPOINT[_this.featureName], {
      onFinished: function onFinished(result) {
        return _this.postHarvestCleanup(result);
      },
      retryDelay: _this.harvestTimeSeconds,
      getPayload: function getPayload(_ref) {
        var retry = _ref.retry,
            opts = _objectWithoutProperties(_ref, _excluded);

        return _this.makeHarvestPayload(retry, opts);
      },
      raw: true
    }, _assertThisInitialized(_this));
    registerHandler(SR_EVENT_EMITTER_TYPES.PAUSE, function () {
      _this.forceStop(_this.mode !== MODE.ERROR);
    }, _this.featureName, _this.ee);
    registerHandler(SR_EVENT_EMITTER_TYPES.ERROR_DURING_REPLAY, function (e) {
      _this.handleError(e);
    }, _this.featureName, _this.ee);
    var _agentRef$init$sessio = agentRef.init.session_replay,
        error_sampling_rate = _agentRef$init$sessio.error_sampling_rate,
        sampling_rate = _agentRef$init$sessio.sampling_rate,
        autoStart = _agentRef$init$sessio.autoStart,
        block_selector = _agentRef$init$sessio.block_selector,
        mask_text_selector = _agentRef$init$sessio.mask_text_selector,
        mask_all_inputs = _agentRef$init$sessio.mask_all_inputs,
        inline_images = _agentRef$init$sessio.inline_images,
        collect_fonts = _agentRef$init$sessio.collect_fonts;

    _this.waitForFlags(['srs', 'sr']).then(function (_ref2) {
      var _ref3 = _slicedToArray(_ref2, 2),
          srMode = _ref3[0],
          entitled = _ref3[1];

      _this.entitled = !!entitled;

      if (!_this.entitled) {
        var _this$recorder3;

        _this.deregisterDrain();

        if ((_this$recorder3 = _this.recorder) !== null && _this$recorder3 !== void 0 && _this$recorder3.recording) {
          _this.abort(ABORT_REASONS.ENTITLEMENTS);

          handle(SUPPORTABILITY_METRIC_CHANNEL, ['SessionReplay/EnabledNotEntitled/Detected'], undefined, FEATURE_NAMES.metrics, _this.ee);
        }

        return;
      }

      _this.drain();

      _this.initializeRecording(srMode);
    }).then(function () {
      if (_this.mode === MODE.OFF) {
        var _this$recorder4;

        (_this$recorder4 = _this.recorder) === null || _this$recorder4 === void 0 ? void 0 : _this$recorder4.stopRecording(); // stop any conservative preload recording launched by instrument

        while ((_this$recorder5 = _this.recorder) !== null && _this$recorder5 !== void 0 && _this$recorder5.getEvents().events.length) {
          var _this$recorder5, _this$recorder6, _this$recorder6$clear, _this$recorder7;

          (_this$recorder6 = _this.recorder) === null || _this$recorder6 === void 0 ? void 0 : (_this$recorder6$clear = (_this$recorder7 = _this$recorder6).clearBuffer) === null || _this$recorder6$clear === void 0 ? void 0 : _this$recorder6$clear.call(_this$recorder7);
        }
      }

      sharedChannel.onReplayReady(_this.mode);
    }); // notify watchers that replay started with the mode

    /** Detect if the default configs have been altered and report a SM.  This is useful to evaluate what the reasonable defaults are across a customer base over time */


    if (!autoStart) handle(SUPPORTABILITY_METRIC_CHANNEL, ['Config/SessionReplay/AutoStart/Modified'], undefined, FEATURE_NAMES.metrics, _this.ee);
    if (collect_fonts === true) handle(SUPPORTABILITY_METRIC_CHANNEL, ['Config/SessionReplay/CollectFonts/Modified'], undefined, FEATURE_NAMES.metrics, _this.ee);
    if (inline_images === true) handle(SUPPORTABILITY_METRIC_CHANNEL, ['Config/SessionReplay/InlineImages/Modifed'], undefined, FEATURE_NAMES.metrics, _this.ee);
    if (mask_all_inputs !== true) handle(SUPPORTABILITY_METRIC_CHANNEL, ['Config/SessionReplay/MaskAllInputs/Modified'], undefined, FEATURE_NAMES.metrics, _this.ee);
    if (block_selector !== '[data-nr-block]') handle(SUPPORTABILITY_METRIC_CHANNEL, ['Config/SessionReplay/BlockSelector/Modified'], undefined, FEATURE_NAMES.metrics, _this.ee);
    if (mask_text_selector !== '*') handle(SUPPORTABILITY_METRIC_CHANNEL, ['Config/SessionReplay/MaskTextSelector/Modified'], undefined, FEATURE_NAMES.metrics, _this.ee);
    handle(SUPPORTABILITY_METRIC_CHANNEL, ['Config/SessionReplay/SamplingRate/Value', sampling_rate], undefined, FEATURE_NAMES.metrics, _this.ee);
    handle(SUPPORTABILITY_METRIC_CHANNEL, ['Config/SessionReplay/ErrorSamplingRate/Value', error_sampling_rate], undefined, FEATURE_NAMES.metrics, _this.ee);
    return _this;
  }

  _createClass(Aggregate, [{
    key: "replayIsActive",
    value: function replayIsActive() {
      var _this$scheduler;

      return Boolean(((_this$scheduler = this.scheduler) === null || _this$scheduler === void 0 ? void 0 : _this$scheduler.started) && this.recorder && this.mode === MODE.FULL && !this.blocked && this.entitled);
    }
  }, {
    key: "handleError",
    value: function handleError(e) {
      if (this.recorder) this.recorder.currentBufferTarget.hasError = true; // run once

      if (this.mode === MODE.ERROR && (globalScope === null || globalScope === void 0 ? void 0 : globalScope.document.visibilityState) === 'visible') {
        this.switchToFull();
      }
    }
  }, {
    key: "switchToFull",
    value: function switchToFull() {
      if (!this.entitled || this.blocked) return;
      this.mode = MODE.FULL; // if the error was noticed AFTER the recorder was already imported....

      if (this.recorder && this.initialized) {
        if (!this.recorder.recording) this.recorder.startRecording();
        this.scheduler.startTimer(this.harvestTimeSeconds);
        this.syncWithSessionManager({
          sessionReplayMode: this.mode
        });
      } else {
        this.initializeRecording(false, true, true);
      }
    }
    /**
     * Evaluate entitlements and sampling before starting feature mechanics, importing and configuring recording library, and setting storage state
     * @param {boolean} entitlements - the true/false state of the "sr" flag from RUM response
     * @param {boolean} errorSample - the true/false state of the error sampling decision
     * @param {boolean} fullSample - the true/false state of the full sampling decision
     * @param {boolean} ignoreSession - whether to force the method to ignore the session state and use just the sample flags
     * @returns {void}
     */

  }, {
    key: "initializeRecording",
    value: function () {
      var _initializeRecording = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(srMode, ignoreSession) {
        var _this$recorder8,
            _this$recorder9,
            _this2 = this;

        var _this$agentRef$runtim, session, timeKeeper, _yield$import, Recorder, _this$recorder10;

        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                this.initialized = true;

                if (this.entitled) {
                  _context.next = 3;
                  break;
                }

                return _context.abrupt("return");

              case 3:
                // if theres an existing session replay in progress, there's no need to sample, just check the entitlements response
                // if not, these sample flags need to be checked
                // if this isnt the FIRST load of a session AND
                // we are not actively recording SR... DO NOT import or run the recording library
                // session replay samples can only be decided on the first load of a session
                // session replays can continue if already in progress
                _this$agentRef$runtim = this.agentRef.runtime, session = _this$agentRef$runtim.session, timeKeeper = _this$agentRef$runtim.timeKeeper;
                this.timeKeeper = timeKeeper;

                if (((_this$recorder8 = this.recorder) === null || _this$recorder8 === void 0 ? void 0 : _this$recorder8.parent.trigger) === TRIGGERS.API && (_this$recorder9 = this.recorder) !== null && _this$recorder9 !== void 0 && _this$recorder9.recording) {
                  this.mode = MODE.FULL;
                } else if (!session.isNew && !ignoreSession) {
                  // inherit the mode of the existing session
                  this.mode = session.state.sessionReplayMode;
                } else {
                  // The session is new... determine the mode the new session should start in
                  this.mode = srMode;
                } // If off, then don't record (early return)


                if (!(this.mode === MODE.OFF)) {
                  _context.next = 8;
                  break;
                }

                return _context.abrupt("return");

              case 8:
                if (this.recorder) {
                  _context.next = 23;
                  break;
                }

                _context.prev = 9;
                _context.next = 12;
                return import(
                /* webpackChunkName: "recorder" */
                '../shared/recorder');

              case 12:
                _yield$import = _context.sent;
                Recorder = _yield$import.Recorder;
                this.recorder = new Recorder(this);
                this.recorder.currentBufferTarget.hasError = this.errorNoticed;
                _context.next = 21;
                break;

              case 18:
                _context.prev = 18;
                _context.t0 = _context["catch"](9);
                return _context.abrupt("return", this.abort(ABORT_REASONS.IMPORT));

              case 21:
                _context.next = 24;
                break;

              case 23:
                this.recorder.parent = this;

              case 24:
                // If an error was noticed before the mode could be set (like in the early lifecycle of the page), immediately set to FULL mode
                if (this.mode === MODE.ERROR && this.errorNoticed) this.mode = MODE.FULL;

                if (this.mode === MODE.FULL) {
                  // If theres preloaded events and we are in full mode, just harvest immediately to clear up space and for consistency
                  if (((_this$recorder10 = this.recorder) === null || _this$recorder10 === void 0 ? void 0 : _this$recorder10.getEvents().type) === 'preloaded') {
                    this.prepUtils().then(function () {
                      _this2.scheduler.runHarvest();
                    });
                  } // FULL mode records AND reports from the beginning, while ERROR mode only records (but does not report).
                  // ERROR mode will do this until an error is thrown, and then switch into FULL mode.
                  // If an error happened in ERROR mode before we've gotten to this stage, it will have already set the mode to FULL


                  if (!this.scheduler.started) {
                    // We only report (harvest) in FULL mode
                    this.scheduler.startTimer(this.harvestTimeSeconds);
                  }
                }

                _context.next = 28;
                return this.prepUtils();

              case 28:
                if (!this.recorder.recording) this.recorder.startRecording();
                this.syncWithSessionManager({
                  sessionReplayMode: this.mode
                });

              case 30:
              case "end":
                return _context.stop();
            }
          }
        }, _callee, this, [[9, 18]]);
      }));

      function initializeRecording(_x, _x2) {
        return _initializeRecording.apply(this, arguments);
      }

      return initializeRecording;
    }()
  }, {
    key: "prepUtils",
    value: function () {
      var _prepUtils = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2() {
        var _yield$import2, gzipSync, strToU8;

        return _regeneratorRuntime.wrap(function _callee2$(_context2) {
          while (1) {
            switch (_context2.prev = _context2.next) {
              case 0:
                _context2.prev = 0;
                _context2.next = 3;
                return import(
                /* webpackChunkName: "compressor" */
                'fflate');

              case 3:
                _yield$import2 = _context2.sent;
                gzipSync = _yield$import2.gzipSync;
                strToU8 = _yield$import2.strToU8;
                this.gzipper = gzipSync;
                this.u8 = strToU8;
                _context2.next = 12;
                break;

              case 10:
                _context2.prev = 10;
                _context2.t0 = _context2["catch"](0);

              case 12:
              case "end":
                return _context2.stop();
            }
          }
        }, _callee2, this, [[0, 10]]);
      }));

      function prepUtils() {
        return _prepUtils.apply(this, arguments);
      }

      return prepUtils;
    }()
  }, {
    key: "makeHarvestPayload",
    value: function makeHarvestPayload(shouldRetryOnFail, opts) {
      var _this$timeKeeper,
          _this3 = this;

      if (!this.recorder || !((_this$timeKeeper = this.timeKeeper) !== null && _this$timeKeeper !== void 0 && _this$timeKeeper.ready) || !this.recorder.hasSeenSnapshot) return;
      var recorderEvents = this.recorder.getEvents(); // get the event type and use that to trigger another harvest if needed

      if (!recorderEvents.events.length || this.mode !== MODE.FULL || this.blocked) return;
      var payload = this.getHarvestContents(recorderEvents);

      if (!payload.body.length) {
        this.recorder.clearBuffer();
        return;
      }

      handle(SUPPORTABILITY_METRIC_CHANNEL, ['SessionReplay/Harvest/Attempts'], undefined, FEATURE_NAMES.metrics, this.ee);
      var len = 0;

      if (!!this.gzipper && !!this.u8) {
        payload.body = this.gzipper(this.u8("[".concat(payload.body.map(function (_ref4) {
          var __serialized = _ref4.__serialized,
              e = _objectWithoutProperties(_ref4, _excluded2);

          if (e.__newrelic && __serialized) return __serialized;

          var output = _objectSpread({}, e);

          if (!output.__newrelic) {
            output.__newrelic = buildNRMetaNode(e.timestamp, _this3.timeKeeper);
            output.timestamp = _this3.timeKeeper.correctAbsoluteTimestamp(e.timestamp);
          }

          return stringify(output);
        }).join(','), "]")));
        len = payload.body.length;
        this.scheduler.opts.gzip = true;
      } else {
        payload.body = payload.body.map(function (_ref5) {
          var __serialized = _ref5.__serialized,
              node = _objectWithoutProperties(_ref5, _excluded3);

          if (node.__newrelic) return node;

          var output = _objectSpread({}, node);

          output.__newrelic = buildNRMetaNode(node.timestamp, _this3.timeKeeper);
          output.timestamp = _this3.timeKeeper.correctAbsoluteTimestamp(node.timestamp);
          return output;
        });
        len = stringify(payload.body).length;
        this.scheduler.opts.gzip = false;
      }

      if (len > MAX_PAYLOAD_SIZE) {
        this.abort(ABORT_REASONS.TOO_BIG, len);
        return;
      } // TODO -- Gracefully handle the buffer for retries.


      if (!this.agentRef.runtime.session.state.sessionReplaySentFirstChunk) this.syncWithSessionManager({
        sessionReplaySentFirstChunk: true
      });
      this.recorder.clearBuffer();
      if (recorderEvents.type === 'preloaded') this.scheduler.runHarvest(opts);
      return [payload];
    }
  }, {
    key: "getCorrectedTimestamp",
    value: function getCorrectedTimestamp(node) {
      if (!(node !== null && node !== void 0 && node.timestamp)) return;
      if (node.__newrelic) return node.timestamp;
      return this.timeKeeper.correctAbsoluteTimestamp(node.timestamp);
    }
  }, {
    key: "getHarvestContents",
    value: function getHarvestContents(recorderEvents) {
      var _recorderEvents, _this$agentRef$info$j, _events, _events$, _events2, _agentRuntime$appMeta, _agentRuntime$appMeta2, _agentRuntime$session;

      (_recorderEvents = recorderEvents) !== null && _recorderEvents !== void 0 ? _recorderEvents : recorderEvents = this.recorder.getEvents();
      var events = recorderEvents.events;
      var agentRuntime = this.agentRef.runtime;
      var endUserId = (_this$agentRef$info$j = this.agentRef.info.jsAttributes) === null || _this$agentRef$info$j === void 0 ? void 0 : _this$agentRef$info$j['enduser.id']; // do not let the first node be a full snapshot node, since this NEEDS to be preceded by a meta node
      // we will manually inject it if this happens

      var payloadStartsWithFullSnapshot = ((_events = events) === null || _events === void 0 ? void 0 : (_events$ = _events[0]) === null || _events$ === void 0 ? void 0 : _events$.type) === RRWEB_EVENT_TYPES.FullSnapshot;

      if (payloadStartsWithFullSnapshot && !!this.recorder.lastMeta) {
        recorderEvents.hasMeta = true;
        events.unshift(this.recorder.lastMeta); // --> pushed the meta from a previous payload into newer payload... but it still has old timestamps

        this.recorder.lastMeta = undefined;
      } // do not let the last node be a meta node, since this NEEDS to precede a snapshot
      // we will manually inject it later if we find a payload that is missing a meta node


      var payloadEndsWithMeta = ((_events2 = events[events.length - 1]) === null || _events2 === void 0 ? void 0 : _events2.type) === RRWEB_EVENT_TYPES.Meta;

      if (payloadEndsWithMeta) {
        this.recorder.lastMeta = events[events.length - 1];
        events = events.slice(0, events.length - 1);
        recorderEvents.hasMeta = !!events.find(function (x) {
          return x.type === RRWEB_EVENT_TYPES.Meta;
        });
      }

      var relativeNow = now();
      var firstEventTimestamp = this.getCorrectedTimestamp(events[0]); // from rrweb node

      var lastEventTimestamp = this.getCorrectedTimestamp(events[events.length - 1]); // from rrweb node
      // from rrweb node || from when the harvest cycle started

      var firstTimestamp = firstEventTimestamp || Math.floor(this.timeKeeper.correctAbsoluteTimestamp(recorderEvents.cycleTimestamp));
      var lastTimestamp = lastEventTimestamp || Math.floor(this.timeKeeper.correctRelativeTimestamp(relativeNow));
      var agentMetadata = ((_agentRuntime$appMeta = agentRuntime.appMetadata) === null || _agentRuntime$appMeta === void 0 ? void 0 : (_agentRuntime$appMeta2 = _agentRuntime$appMeta.agents) === null || _agentRuntime$appMeta2 === void 0 ? void 0 : _agentRuntime$appMeta2[0]) || {};
      return {
        qs: {
          browser_monitoring_key: this.agentRef.info.licenseKey,
          type: 'SessionReplay',
          app_id: this.agentRef.info.applicationID,
          protocol_version: '0',
          timestamp: firstTimestamp,
          attributes: encodeObj(_objectSpread(_objectSpread(_objectSpread(_objectSpread({}, !!this.gzipper && !!this.u8 && {
            content_encoding: 'gzip'
          }), agentMetadata.entityGuid && {
            entityGuid: agentMetadata.entityGuid
          }), {}, {
            harvestId: [(_agentRuntime$session = agentRuntime.session) === null || _agentRuntime$session === void 0 ? void 0 : _agentRuntime$session.state.value, agentRuntime.ptid, agentRuntime.harvestCount].filter(function (x) {
              return x;
            }).join('_'),
            'replay.firstTimestamp': firstTimestamp,
            'replay.lastTimestamp': lastTimestamp,
            'replay.nodes': events.length,
            'session.durationMs': agentRuntime.session.getDuration(),
            agentVersion: agentRuntime.version,
            session: agentRuntime.session.state.value,
            rst: relativeNow,
            hasMeta: recorderEvents.hasMeta || false,
            hasSnapshot: recorderEvents.hasSnapshot || false,
            hasError: recorderEvents.hasError || false,
            isFirstChunk: agentRuntime.session.state.sessionReplaySentFirstChunk === false,
            decompressedBytes: recorderEvents.payloadBytesEstimation,
            invalidStylesheetsDetected: stylesheetEvaluator.invalidStylesheetsDetected,
            inlinedAllStylesheets: recorderEvents.inlinedAllStylesheets,
            'rrweb.version': RRWEB_VERSION,
            'payload.type': recorderEvents.type
          }, endUserId && {
            'enduser.id': this.obfuscator.obfuscateString(endUserId)
          }), {}, {
            currentUrl: this.obfuscator.obfuscateString(cleanURL('' + location)) // The Query Param is being arbitrarily limited in length here.  It is also applied when estimating the size of the payload in getPayloadSize()

          }), QUERY_PARAM_PADDING).substring(1) // remove the leading '&'

        },
        body: events
      };
    }
  }, {
    key: "postHarvestCleanup",
    value: function postHarvestCleanup(result) {
      // The mutual decision for now is to stop recording and clear buffers if ingest is experiencing 429 rate limiting
      if (result.status === 429) {
        this.abort(ABORT_REASONS.TOO_MANY);
      }

      if (this.blocked) this.scheduler.stopTimer(true);
    }
    /**
     * Forces the agent into OFF mode so that changing tabs or navigating
     * does not restart the recording. This is used when the customer calls
     * the stopRecording API.
     */

  }, {
    key: "forceStop",
    value: function forceStop(forceHarvest) {
      var _this$recorder11, _this$recorder11$stop;

      if (forceHarvest) this.scheduler.runHarvest();
      this.mode = MODE.OFF;
      (_this$recorder11 = this.recorder) === null || _this$recorder11 === void 0 ? void 0 : (_this$recorder11$stop = _this$recorder11.stopRecording) === null || _this$recorder11$stop === void 0 ? void 0 : _this$recorder11$stop.call(_this$recorder11);
      this.syncWithSessionManager({
        sessionReplayMode: this.mode
      });
    }
    /** Abort the feature, once aborted it will not resume */

  }, {
    key: "abort",
    value: function abort() {
      var _this$recorder12, _this$recorder12$stop, _this$recorder13, _this$recorder13$clea;

      var reason = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
      var data = arguments.length > 1 ? arguments[1] : undefined;
      warn(33, reason.message);
      handle(SUPPORTABILITY_METRIC_CHANNEL, ["SessionReplay/Abort/".concat(reason.sm), data], undefined, FEATURE_NAMES.metrics, this.ee);
      this.blocked = true;
      this.mode = MODE.OFF;
      (_this$recorder12 = this.recorder) === null || _this$recorder12 === void 0 ? void 0 : (_this$recorder12$stop = _this$recorder12.stopRecording) === null || _this$recorder12$stop === void 0 ? void 0 : _this$recorder12$stop.call(_this$recorder12);
      this.syncWithSessionManager({
        sessionReplayMode: this.mode
      });
      (_this$recorder13 = this.recorder) === null || _this$recorder13 === void 0 ? void 0 : (_this$recorder13$clea = _this$recorder13.clearTimestamps) === null || _this$recorder13$clea === void 0 ? void 0 : _this$recorder13$clea.call(_this$recorder13);
      this.ee.emit('REPLAY_ABORTED');

      while ((_this$recorder14 = this.recorder) !== null && _this$recorder14 !== void 0 && _this$recorder14.getEvents().events.length) {
        var _this$recorder14, _this$recorder15, _this$recorder15$clea, _this$recorder16;

        (_this$recorder15 = this.recorder) === null || _this$recorder15 === void 0 ? void 0 : (_this$recorder15$clea = (_this$recorder16 = _this$recorder15).clearBuffer) === null || _this$recorder15$clea === void 0 ? void 0 : _this$recorder15$clea.call(_this$recorder16);
      }
    }
  }, {
    key: "syncWithSessionManager",
    value: function syncWithSessionManager() {
      var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
      this.agentRef.runtime.session.write(state);
    }
  }]);

  return Aggregate;
}(AggregateBase);

_defineProperty(Aggregate, "featureName", FEATURE_NAME);